import { ApiAddress } from './ApiAddress';
import { ApiInventoryItem } from './ApiInventoryItem';
import { Contact, IPrivateApiFields, TimeWindow } from './CommonTypes';
import { PaginationMetadata } from './Pagination';

/**
 * Numerical enum of task priority value. Sequence starts at 1
 * representing a high priority task
 * @category API Types
 */
export enum TaskPriority {
    HIGH = 1,
    MEDIUM,
    LOW
}

/**
 * Numerical enum of task statuses.
 * @category API Types
 */
export enum TaskStatus {
    UNASSIGNED,
    ASSIGNED,
    IN_PROGRESS,
    COMPLETED,
    FAILED
}

/**
 * Enum of supported task status conditions
 * @category API Types
 */
export enum TaskStatusCondition {
    UNASSIGNED,
    DISPATCHED,
    IN_PROGRESS,
    COMPLETED,
    CANCELLED,
    PLANNED
}

/**
 * Enum of supported task types returned by `wise-api`
 * @category API Types
 */
export enum TaskTypes {
    DEPOT = 'depot',
    PICKUP = 'pickup',
    DELIVERY = 'delivery',
    TWOPART = 'twoPart'
}

/**
 * Enum of task types supported for external task type dropdown
 * @category Add Task Modal External Task Type Dropdown
 */
export enum AddTaskTypes {
    PICKUP = TaskTypes.PICKUP,
    DELIVERY = TaskTypes.DELIVERY,
    TWOPART = TaskTypes.TWOPART
}

/**
 * Enum of task types supported for address locations
 * @category Add Task Modal Address Types
 */
export enum AddressTaskTypes {
    PICKUP = 'pickup',
    DELIVERY = 'delivery'
}

/**
 * Enum of supported methods to input location data
 * @category API Types
 */
export enum InputLocationMethods {
    CUSTOMER_DB = 'customer_db',
    GOOGLE = 'google',
    MANUAL = 'manual'
}

/**
 * Constructs a record that can have `pickup` or `delivery` properties, each of type T
 */
export type TaskTypeRecord<T> = Partial<Record<'pickup' | 'delivery', T>>;

export interface ApiNote {
    /**
     * the note scope
     */
    scope?: string;
    /**
     * the note title
     */
    title?: string;
    /**
     * The note text
     */
    text?: string;
}

export type ApiCompartment = {
    /**
     * the compartment size
     */
    size: number;

    /**
     * the compartment type
     */
    compartmentType: string;
};

/**
 * Type agnostic task props fields
 * @category API Types
 */
interface TypeAgnosticTaskProps {
    /**
     * the task priority
     */
    priority: TaskPriority;
    /**
     * the task size
     */
    size: number;
    /**
     * the task weight
     */
    weight: number;
    /**
     * indicates the task is eligible for pairing
     */
    pairingEligible: boolean;
    /**
     * the task workflows
     */
    workflow?: {
        /**
         * whether the task workflow requires a signature
         */
        requireSignature?: boolean;
        /**
         * whether the task workflow prompts for a signature
         */
        promptSignature?: boolean;
    };
    /**
     * the task pickup time. Included in type agnostic props as it
     * will be included as an explicit null in delivery tasks.
     */
    pickupTime: string | null;
    /**
     * the task delivery time. Included in type agnostic props as it
     * will be included as an explicit null in pickup tasks.
     */
    deliveryTime: string | null;
    /**
     * the task notes
     */
    notes?: ApiNote[];
    /**
     * the task external links
     */
    externalLinks?: string[];
}

export interface ApiInvoice {
    invoiceNumber: string;
    amountDue: number;
    invoiceId: string;
}

/**
 * Pickup task props fields
 * @category API Types
 */
export interface PickupTaskProps extends TypeAgnosticTaskProps {
    /**
     * the pickup service time
     */
    pickupServiceTime: string;
    /**
     * the pickup time windows
     */
    pickupWindow: Array<TimeWindow>;
    /**
     * the pickup inventory
     */
    pickupInventory?: Array<ApiInventoryItem>;
    /**
     * the original pickup task props
     */
    originalPickup?: Pick<ApiTask, 'labels'>;
    /**
     * the pickup invoices
     */
    pickupInvoices?: ApiInvoice[];
}

/**
 * Delivery task props fields
 * @category API Types
 */
export interface DeliveryTaskProps extends TypeAgnosticTaskProps {
    /**
     * the delivery service time
     */
    deliveryServiceTime: string;
    /**
     * the delivery time windows
     */
    deliveryWindow: Array<TimeWindow>;
    /**
     * the delivery inventory
     */
    deliveryInventory?: Array<ApiInventoryItem>;
    /**
     * the original pickup task props
     */
    originaldelivery?: Pick<ApiTask, 'labels'>;
    /**
     * the delivery invoices
     */
    deliveryInvoices?: ApiInvoice[];
}

// JSDoc below type declaration is intentional.
// Better-docs cannot convert type intersections and unions
export type ApiTaskProps =
    | PickupTaskProps
    | DeliveryTaskProps
    | (PickupTaskProps & DeliveryTaskProps);

/**
 * The API Task props type. This type can be used for the props of a pickup,
 * delivery or two-part task.
 * @category API Types
 * @typedef ApiTaskProps
 * @type {(PickupTaskProps | DeliveryTaskProps | {...PickupTaskProps, ...DeliveryTaskProps})}
 */

/**
 * Type agnostic task fields
 * @category API Types
 */
interface TypeAgnosticTask extends IPrivateApiFields {
    /**
     * the task id
     */
    id: string;
    /**
     * the task deduplication id
     */
    deduplicationId: string;
    /**
     * the task consolidation id
     */
    consolidationId: string;
    /**
     * the task route date. ISO-8601 date format
     */
    routeDate: string;
    /**
     * the task name
     */
    name: string;
    /**
     * the task status
     */
    status: TaskStatus;
    /**
     * the task status condition, calculated based on `status` and `routeId`.
     *
     * Generally, the `taskStatus` is set by using `taskUtils.getTaskStatus()`
     * on the `TasksApi.getTask()` or `TasksApi.get()` response
     *
     * The `Task.taskStatusCondition` data class property can calculate
     * it directly if not available
     */
    taskStatus?: TaskStatusCondition;
    /**
     * the task eid
     */
    eid?: number;
    /**
     * the task euid
     */
    euid?: string | null;
    /**
     * the task route id
     */
    routeEid?: string;
    /**
     * the eid of the vehicle associted to this task
     */
    vehicleEid?: string;
    /**
     * indicates the task is locked to a route
     */
    locked: boolean;
    /**
     * indicates the task is locked to a specific position in a route
     */
    orderLocked: boolean;
    /**
     * id of the route this task is planned for
     */
    routeId?: string;
    /**
     * the task forecast type
     */
    forecastType?: string;
    /**
     * types of vehicles required by this task
     */
    vehicleTypes: string[];
    /**
     * the task labels
     */
    labels: string[] | null;
    /**
     * the task eorder
     */
    eorder?: number;
    /**
     * client this task belongs to
     */
    client: string;
    /**
     * notification settings for this task
     */
    notification: {
        /**
         * indicates sms notifications are enabled
         */
        sms: boolean;
        /**
         * indicates email notifications are enabled
         */
        email: boolean;
    };
    /**
     * the 2-part task id, optionally set by Interstellar
     * primarily to uniquely track the delivery and pickup task data for loops as keys
     */
    twoPartId?: string;
    /**
     * the external task type, optionally set by a client on task creation
     */
    externalTaskType?: string;
    /**
     * ISO-8601 timestamp of when the task was created
     */
    createdAt: string;
    /**
     * ISO-8601 timestamp of when the task was last updated
     */
    updatedAt: string;
    /**
     * Compartment list when a client has enabled multiple compartments
     */
    sizeByCompartment: ApiCompartment[];
    /**
     * The task equipment Id
     */
    equipmentId: string | null;
    /**
     * The task equipment payor of freight
     */
    payerOfFreight: string | null;
    /**
     * The task equipment reservation
     */
    reservation: string | null;
    /**
     * The task equipment car kind
     */
    carKind: string | null;
    /**
     * The task equipment drop/stay status
     */
    dropStay: string | null;
}

/**
 * Pickup task fields
 * @category API Types
 */
export interface PickupTask extends TypeAgnosticTask {
    /**
     * the task pickup contact
     */
    pickupContact: Contact;
    /**
     * the task pickup location
     */
    pickupLocation: ApiAddress;
    /**
     * the pickup task props
     */
    props: TypeAgnosticTaskProps & PickupTaskProps;
    /**
     * the original delivery task props
     */
    originalProps?: Partial<TypeAgnosticTaskProps & PickupTaskProps>;
    /**
     * The pickup customers' euid
     */
    pickupEuid: string;
}

/**
 * Delivery task fields
 * @category API Types
 */
export interface DeliveryTask extends TypeAgnosticTask {
    /**
     * the task delivery contact
     */
    deliveryContact: Contact;
    /**
     * the task delivery location
     */
    deliveryLocation: ApiAddress;
    /**
     * the delivery task props
     */
    props: TypeAgnosticTaskProps & DeliveryTaskProps;
    /**
     * the original delivery task props
     */
    originalProps?: Partial<TypeAgnosticTaskProps & DeliveryTaskProps>;
    /**
     * The delivery customers' euid
     */
    deliveryEuid: string;
}

// JSDoc below type declaration is intentional.
// Better-docs cannot convert type intersections and unions
export type ApiTask = PickupTask | DeliveryTask | (PickupTask & DeliveryTask);

/**
 * The API Task type. This type can be used for a pickup, a delivery,
 * or a two-part task.
 * @category API Types
 * @typedef ApiTask
 * @type {(PickupTask | DeliveryTask | {...PickupTask, ...DeliveryTask})}
 */

export interface ApiLiveDispatch {
    /**
     *  The API Task type. This type can be used for a pickup, a delivery.
     */
    data: ApiTask[];

    /**
     * This is for pagination meta data for API
     */
    meta?: PaginationMetadata;
}

export interface TypeAgnosticStopData {
    /**
     * The stop marker task client route ID
     */
    clientRouteId: string;
    /**
     * Whether the stop marker task is for a two-part task
     */
    isTwoPart: boolean;
    /**
     * The stop marker task route date
     */
    routeDate: string;
    /**
     * The stop marker task ID
     */
    taskId: string;
    /**
     * Whether the stop marker task is planned
     */
    isPlanned: boolean;
    /**
     * Whether the stop marker task is high priority
     */
    isHighPriority: boolean;
    /**
     * Serializes back to JSON
     */
    toJSON: () => () => ApiTask;
}

export interface TypeSpecificStopData extends TypeAgnosticStopData {
    /**
     * The stop marker task client route task ID
     */
    clientRouteTaskId: string;
    /**
     * The stop marker task latitude
     */
    lat: number | null;
    /**
     * The stop marker task longitude
     */
    lng: number | null;
    /**
     * The stop marker task label
     */
    label: string | null;
}
